import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateQuiz, Quiz, Question, QuizGenerationRequest, regenerateQuestion as regenerateQuestionService, factCheckQuiz } from '../services/quizService';
import { createQuiz, type CreateQuizRequest, type StoredQuestion } from '../services/quizStorageService';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';

interface QuestionWithState extends Question {
  isRegenerating?: boolean;
}

interface QuizWithState extends Quiz {
  questions: QuestionWithState[];
}

// localStorage helpers with TTL
const saveTemporaryQuiz = (quiz: QuizWithState, prompt: string, settings: any) => {
  localStorage.setItem('tempQuiz', JSON.stringify({
    quiz,
    prompt,
    settings,
    expiry: Date.now() + 600000 // 10 minutes
  }));
};

const getTemporaryQuiz = (): { quiz: QuizWithState; prompt: string; settings: any } | null => {
  const data = localStorage.getItem('tempQuiz');
  if (!data) return null;

  try {
    const parsed = JSON.parse(data);
    if (Date.now() > parsed.expiry) {
      localStorage.removeItem('tempQuiz');
      return null;
    }
    return { quiz: parsed.quiz, prompt: parsed.prompt, settings: parsed.settings };
  } catch {
    localStorage.removeItem('tempQuiz');
    return null;
  }
};

const clearTemporaryQuiz = () => {
  localStorage.removeItem('tempQuiz');
};

const CreateQuiz: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, signIn, user } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [quiz, setQuiz] = useState<QuizWithState | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingStage, setLoadingStage] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isSavingQuiz, setIsSavingQuiz] = useState(false);
  const [savedQuizId, setSavedQuizId] = useState<string | null>(null);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [isFactCheckExpanded, setIsFactCheckExpanded] = useState(false);
  const [isFactChecking, setIsFactChecking] = useState(false);

  // Pro features state
  const [difficulty, setDifficulty] = useState<'easy' | 'normal' | 'hard'>('normal');
  const [toneOfVoice, setToneOfVoice] = useState<'professional' | 'casual' | 'fun'>('casual');
  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const [language, setLanguage] = useState<'English' | 'Spanish' | 'French' | 'German' | 'Norwegian'>('English');
  const [qaCheck, setQaCheck] = useState(false);
  const [isProFeaturesExpanded, setIsProFeaturesExpanded] = useState(true);

  // Loading stages and messages
  const loadingStages = [
    { message: "Analyzing your topic...", icon: "🔍" },
    { message: "Crafting brilliant questions...", icon: "💡" },
    { message: "Adding some challenge...", icon: "🎯" },
    { message: "Making it engaging...", icon: "✨" },
    { message: "Finalizing your quiz...", icon: "🎉" }
  ];

  const topicFacts = [
    "Did you know? The average person retains 65% of information when it's presented visually.",
    "Fun fact: Active recall through quizzes can improve learning retention by up to 50%.",
    "Quiz tip: Multiple choice questions with 4 options have optimal difficulty balance.",
    "Learning fact: Spaced repetition with quizzes enhances long-term memory formation.",
    "Brain boost: Testing yourself is more effective than re-reading for memory retention."
  ];

  // Unified restoration after login
  useEffect(() => {
    if (!isAuthenticated) return;

    const tempData = getTemporaryQuiz();
    if (!tempData) return;

    const { quiz: savedQuiz, prompt: savedPrompt, settings } = tempData;

    // Restore quiz if it exists
    if (savedQuiz) {
      setQuiz(savedQuiz);
      setPrompt(savedPrompt);
      clearTemporaryQuiz();

      // Auto-save the restored quiz
      autoSaveRestoredQuiz(savedQuiz, savedPrompt, settings);
    } else if (savedPrompt) {
      // Just restore form settings for regeneration
      setPrompt(savedPrompt);
      setDifficulty(settings.difficulty || 'normal');
      setToneOfVoice(settings.toneOfVoice || 'casual');
      setNumberOfQuestions(settings.numberOfQuestions || 10);
      setLanguage(settings.language || 'English');
      setQaCheck(settings.qaCheck || false);
      clearTemporaryQuiz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Timer effect for elapsed time
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isGenerating && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isGenerating, startTime]);

  // Progressive loading stages
  useEffect(() => {
    let stageInterval: NodeJS.Timeout;
    if (isGenerating) {
      stageInterval = setInterval(() => {
        setLoadingStage(prev => (prev + 1) % loadingStages.length);
      }, 4500);
    } else {
      setLoadingStage(0);
    }
    return () => clearInterval(stageInterval);
  }, [isGenerating, loadingStages.length]);

  // Rotate facts carousel
  useEffect(() => {
    let factInterval: NodeJS.Timeout;
    if (isGenerating) {
      factInterval = setInterval(() => {
        setCurrentFactIndex(prev => (prev + 1) % topicFacts.length);
      }, 3000); // Change fact every 3 seconds
    } else {
      setCurrentFactIndex(0);
    }
    return () => clearInterval(factInterval);
  }, [isGenerating, topicFacts.length]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    // Check authentication before generating
    if (!isAuthenticated) {
      // Save form state before redirecting to login
      saveTemporaryQuiz(null as any, prompt, {
        difficulty,
        toneOfVoice,
        numberOfQuestions,
        language,
        qaCheck
      });
      signIn();
      return;
    }

    setIsGenerating(true);
    setError(null);
    setStartTime(Date.now());
    setElapsedTime(0);

    try {
      const request: QuizGenerationRequest = {
        prompt,
        difficulty,
        toneOfVoice,
        numberOfQuestions,
        language,
        qaCheck
      };

      console.log('UI: Starting quiz generation...', request);
      const generatedQuiz = await generateQuiz(request);
      console.log('UI: Quiz generation successful!', generatedQuiz);
      setQuiz(generatedQuiz as QuizWithState);
    } catch (err) {
      console.error('UI: Error generating quiz:', err);

      let errorMessage = 'Failed to generate quiz. Please try again.';
      if (err instanceof Error) {
        console.error('UI: Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });

        // Use the specific error message if available
        errorMessage = err.message || errorMessage;
      }

      setError(errorMessage);
    } finally {
      setIsGenerating(false);
      setStartTime(null);
    }
  };

  const removeQuestion = (questionId: string) => {
    if (!quiz) return;

    setQuiz({
      ...quiz,
      questions: quiz.questions.filter(q => q.id !== questionId)
    });
  };

  const regenerateQuestion = async (questionId: string) => {
    if (!quiz) return;

    setQuiz({
      ...quiz,
      questions: quiz.questions.map(q =>
        q.id === questionId ? { ...q, isRegenerating: true } : q
      )
    });

    try {
      // Find the index of the question to regenerate
      const questionIndex = quiz.questions.findIndex(q => q.id === questionId);
      if (questionIndex === -1) return;

      // Prepare quiz context in the format expected by the API
      const quizContext = quiz.questions.map(q => ({
        id: q.id,
        question: q.question,
        answers: q.alternatives,
        correct_index: q.correctAnswer,
      }));

      console.log('UI: Regenerating question with context...', { questionIndex, difficulty, language, toneOfVoice });

      const newQuestion = await regenerateQuestionService({
        quizContext,
        questionIndex,
        difficulty: difficulty === 'normal' ? 'medium' : difficulty,
        toneOfVoice,
        language,
        originalPrompt: prompt,
      });

      console.log('UI: Question regeneration successful!', newQuestion);

      setQuiz(prevQuiz => {
        if (!prevQuiz) return prevQuiz;

        // Find the index of the regenerated question
        const regeneratedIndex = prevQuiz.questions.findIndex(q => q.id === questionId);

        // Clear fact-check findings for this specific question
        const updatedFactCheck = prevQuiz.fact_check ? {
          ...prevQuiz.fact_check,
          findings: prevQuiz.fact_check.findings.filter(
            finding => finding.question_number !== regeneratedIndex + 1
          )
        } : prevQuiz.fact_check;

        return {
          ...prevQuiz,
          fact_check: updatedFactCheck,
          questions: prevQuiz.questions.map(q =>
            q.id === questionId
              ? { ...newQuestion, id: questionId, isRegenerating: false }
              : q
          )
        };
      });
    } catch (err) {
      console.error('UI: Error regenerating question:', err);

      if (err instanceof Error) {
        console.error('UI: Regeneration error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
      }

      // Reset the regenerating state on error
      setQuiz(prevQuiz => {
        if (!prevQuiz) return prevQuiz;
        return {
          ...prevQuiz,
          questions: prevQuiz.questions.map(q =>
            q.id === questionId ? { ...q, isRegenerating: false } : q
          )
        };
      });
    }
  };

  const autoSaveRestoredQuiz = async (quizToSave: QuizWithState, quizPrompt: string, settings: any) => {
    setIsSavingQuiz(true);
    setError(null);

    try {
      const storedQuestions: StoredQuestion[] = quizToSave.questions.map(q => ({
        id: q.id,
        question: q.question,
        answers: q.alternatives,
        correct_index: q.correctAnswer
      }));

      const createRequest: CreateQuizRequest = {
        title: quizToSave.title,
        description: `Quiz about ${quizPrompt}`,
        questions: storedQuestions,
        difficulty: settings.difficulty || difficulty,
        language: settings.language || language,
        is_public: false
      };

      const savedQuiz = await createQuiz(createRequest);
      setSavedQuizId(savedQuiz.quiz_id);

      setTimeout(() => {
        navigate(`/edit-quiz/${savedQuiz.quiz_id}`);
      }, 1000);
    } catch (err) {
      console.error('Failed to auto-save restored quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to save quiz');
    } finally {
      setIsSavingQuiz(false);
    }
  };

  const handleRerunFactCheck = async () => {
    if (!quiz) return;

    setIsFactChecking(true);
    setError(null);

    try {
      const quizContext = quiz.questions.map(q => ({
        id: q.id,
        question: q.question,
        answers: q.alternatives,
        correct_index: q.correctAnswer,
      }));

      console.log('UI: Re-running fact-check...');
      const factCheckResult = await factCheckQuiz({
        questions: quizContext,
        originalPrompt: prompt,
      });
      console.log('UI: Fact-check completed!', factCheckResult);

      setQuiz(prevQuiz => {
        if (!prevQuiz) return prevQuiz;
        return {
          ...prevQuiz,
          fact_check: factCheckResult,
        };
      });
    } catch (err) {
      console.error('UI: Error re-running fact-check:', err);
      setError(err instanceof Error ? err.message : 'Failed to re-run fact-check');
    } finally {
      setIsFactChecking(false);
    }
  };

  const handleSaveQuiz = async () => {
    if (!quiz) return;

    if (!isAuthenticated) {
      // Save quiz data before redirecting to login
      saveTemporaryQuiz(quiz, prompt, {
        difficulty,
        toneOfVoice,
        numberOfQuestions,
        language,
        qaCheck
      });
      signIn();
      return;
    }

    setIsSavingQuiz(true);
    setError(null);

    try {
      // Transform questions to match the storage format
      const storedQuestions: StoredQuestion[] = quiz.questions.map(q => ({
        id: q.id,
        question: q.question,
        answers: q.alternatives,
        correct_index: q.correctAnswer
      }));

      const createRequest: CreateQuizRequest = {
        title: quiz.title,
        description: `Quiz about ${prompt}`,
        questions: storedQuestions,
        difficulty,
        language,
        is_public: false
      };

      console.log('Saving quiz...', createRequest);
      const savedQuiz = await createQuiz(createRequest);
      console.log('Quiz saved successfully!', savedQuiz);
      setSavedQuizId(savedQuiz.quiz_id);

      // Show success message briefly then redirect to dashboard
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      console.error('Failed to save quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to save quiz');
    } finally {
      setIsSavingQuiz(false);
    }
  };

  // Skeleton question card component
  const SkeletonQuestionCard: React.FC<{ index: number }> = ({ index }) => {
    return (
      <div className="card p-4 sm:p-6 animate-pulse">
        <div className="flex justify-between items-start mb-4 gap-4">
          <div className="flex-1">
            <div className="h-5 sm:h-6 bg-gradient-to-r from-orange-200 to-gray-200 rounded-lg mb-2 animate-shimmer"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4 animate-shimmer" style={{animationDelay: '0.2s'}}></div>
          </div>
          <div className="flex gap-2">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gray-200 rounded-full animate-shimmer" style={{animationDelay: '0.4s'}}></div>
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gray-200 rounded-full animate-shimmer" style={{animationDelay: '0.6s'}}></div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {[0, 1, 2, 3].map((altIndex) => (
            <div
              key={altIndex}
              className="p-3 rounded-xl border bg-gray-50 border-gray-200 animate-shimmer"
              style={{animationDelay: `${0.8 + altIndex * 0.2}s`}}
            >
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const QuestionCard: React.FC<{ question: QuestionWithState; index: number }> = ({ question, index }) => {
    // Check if this question has findings in fact-check
    const questionFinding = qaCheck && quiz?.fact_check?.findings.find(
      finding => finding.question_number === index + 1
    );

    return (
      <div className={`card p-4 sm:p-6 transition-all duration-300 hover:shadow-xl ${
        questionFinding
          ? 'bg-amber-50/30 border-amber-300 hover:border-amber-400'
          : 'hover:border-orange-300'
      } ${
        question.isRegenerating ? 'opacity-50' : 'opacity-100'
      }`}>
        {questionFinding && (
          <div className="mb-3 px-3 py-2 bg-amber-100/60 border border-amber-300 rounded-lg flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-xs sm:text-sm text-amber-800 font-medium">
                AI Fact Check: Issue Found
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                {questionFinding.message}
              </p>
            </div>
          </div>
        )}
        <div className="flex justify-between items-start mb-4 gap-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex-1">
            Question {index + 1}: {question.question}
          </h3>
          <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={() => regenerateQuestion(question.id)}
              disabled={question.isRegenerating}
              className="p-1.5 sm:p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              title="Regenerate question"
            >
              {question.isRegenerating ? (
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button>
            <button
              onClick={() => removeQuestion(question.id)}
              disabled={question.isRegenerating}
              className="p-1.5 sm:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              title="Remove question"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {question.alternatives.map((alternative, altIndex) => (
            <div
              key={altIndex}
              className={`p-2.5 sm:p-3 rounded-xl border transition-all duration-200 text-sm sm:text-base ${
                altIndex === question.correctAnswer
                  ? 'bg-emerald-50 border-emerald-400 text-emerald-800 shadow-sm'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-orange-300'
              } ${question.isRegenerating ? 'opacity-50' : 'opacity-100'}`}
            >
              <span className="font-medium">
                {String.fromCharCode(65 + altIndex)}: {alternative}
              </span>
              {altIndex === question.correctAnswer && (
                <span className="ml-2 text-emerald-600">✓</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <Header />
      <div className="page-content">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              Create Your Quiz
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              Enter a topic and we'll generate a quiz for you using AI!
            </p>
          </div>

          {!quiz ? (
            <div className="card-xl max-w-5xl mx-auto">
              <div className="card-content">
                {/* Sign-in gate for non-authenticated users */}
                {!isAuthenticated && (
                  <div className="mb-6 p-6 sm:p-8 bg-gradient-to-br from-orange-50 via-blue-50 to-orange-50 border-2 border-orange-300 rounded-2xl text-center">
                    <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      Sign In Required
                    </h3>
                    <p className="text-gray-700 text-sm sm:text-base mb-6 max-w-md mx-auto">
                      Create a free account to start generating AI-powered quizzes in seconds!
                    </p>
                    <button
                      onClick={signIn}
                      className="btn-primary py-3 px-8 text-base sm:text-lg shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      Sign In to Get Started
                    </button>
                    <p className="text-xs text-gray-500 mt-4">
                      Free during beta • No credit card required
                    </p>
                  </div>
                )}
                <form onSubmit={handleSubmit} className={`space-y-6 transition-opacity ${!isAuthenticated ? 'opacity-50 pointer-events-none' : ''}`}>
                  <div>
                    <label htmlFor="prompt" className="label text-base sm:text-lg">
                      What would you like your quiz to be about?
                    </label>
                    <textarea
                      id="prompt"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={!isAuthenticated ? "Sign in above to start creating quizzes..." : "Enter your quiz topic here... (e.g., 'Ancient Egypt', 'JavaScript programming', 'Soccer World Cup')"}
                      className="textarea text-base"
                      rows={4}
                      disabled={isGenerating || !isAuthenticated}
                    />
                    {error && (
                      <div className="mt-3 p-3 sm:p-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm sm:text-base">
                        {error}
                      </div>
                    )}
                  </div>

                  {/* Number of Questions - Basic Setting */}
                  <div className="space-y-2">
                    <label htmlFor="numberOfQuestions" className="flex items-center text-sm sm:text-base font-semibold text-gray-800">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                      Number of Questions
                    </label>
                    <div className="relative max-w-sm">
                      <input
                        id="numberOfQuestions"
                        type="number"
                        min="1"
                        max="25"
                        value={numberOfQuestions}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setNumberOfQuestions(1);
                          } else {
                            const num = parseInt(val);
                            setNumberOfQuestions(num);
                          }
                        }}
                        onBlur={(e) => {
                          const num = parseInt(e.target.value) || 1;
                          setNumberOfQuestions(Math.min(25, Math.max(1, num)));
                        }}
                        className="input pr-20 text-sm sm:text-base"
                        disabled={isGenerating || !isAuthenticated}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-orange-600 font-medium bg-orange-100 px-2 py-1 rounded">max 25</span>
                    </div>
                  </div>

                {/* Pro Features Section */}
                <div className="bg-gradient-to-br from-orange-50 to-gray-50 rounded-2xl border-2 border-orange-200 overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => setIsProFeaturesExpanded(!isProFeaturesExpanded)}
                    disabled={!isAuthenticated}
                    className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <div className="badge-gradient text-xs sm:text-sm whitespace-nowrap">
                        ✨ Pro Features <span className="hidden sm:inline font-normal opacity-90">(Free in beta)</span>
                      </div>
                      <span className="text-xs sm:text-sm text-gray-700 font-medium">Customize your quiz</span>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 text-orange-600 transition-transform duration-300 flex-shrink-0 ${isProFeaturesExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <div className={`transition-all duration-300 ease-in-out ${isProFeaturesExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                    <div className="p-4 sm:p-6 pt-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {/* Difficulty */}
                        <div className="space-y-2">
                          <label className="flex items-center text-xs sm:text-sm font-semibold text-gray-800">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Difficulty Level
                          </label>
                          <div className="flex gap-2">
                            {[
                              { level: 'easy', emoji: '😊' },
                              { level: 'normal', emoji: '🎯' },
                              { level: 'hard', emoji: '🔥' }
                            ].map(({ level, emoji }) => (
                              <button
                                key={level}
                                type="button"
                                onClick={() => setDifficulty(level as 'easy' | 'normal' | 'hard')}
                                disabled={isGenerating || !isAuthenticated}
                                className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-3 rounded-lg border-2 font-semibold text-xs sm:text-sm transition-all ${
                                  difficulty === level
                                    ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                <span className="mr-0.5 sm:mr-1">{emoji}</span>
                                <span className="hidden sm:inline">{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                                <span className="sm:hidden">{level.charAt(0).toUpperCase()}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Tone of Voice */}
                        <div className="space-y-2">
                          <label className="flex items-center text-xs sm:text-sm font-semibold text-gray-800">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                            Tone of Voice
                          </label>
                          <div className="flex gap-2">
                            {[
                              { tone: 'professional', emoji: '💼' },
                              { tone: 'casual', emoji: '😎' },
                              { tone: 'fun', emoji: '🎉' }
                            ].map(({ tone, emoji }) => (
                              <button
                                key={tone}
                                type="button"
                                onClick={() => setToneOfVoice(tone as 'professional' | 'casual' | 'fun')}
                                disabled={isGenerating || !isAuthenticated}
                                className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-3 rounded-lg border-2 font-semibold text-xs sm:text-sm transition-all ${
                                  toneOfVoice === tone
                                    ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                <span className="mr-0.5 sm:mr-1">{emoji}</span>
                                <span className="hidden sm:inline">{tone.charAt(0).toUpperCase() + tone.slice(1)}</span>
                                <span className="sm:hidden">{tone.charAt(0).toUpperCase()}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Language */}
                        <div className="space-y-2 sm:col-span-2 lg:col-span-3">
                          <label className="flex items-center text-xs sm:text-sm font-semibold text-gray-800">
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                            </svg>
                            Language
                          </label>
                          <div className="flex gap-2 flex-wrap">
                            {[
                              { lang: 'English', flag: '🇬🇧' },
                              { lang: 'Spanish', flag: '🇪🇸' },
                              { lang: 'French', flag: '🇫🇷' },
                              { lang: 'German', flag: '🇩🇪' },
                              { lang: 'Norwegian', flag: '🇳🇴' }
                            ].map(({ lang, flag }) => (
                              <button
                                key={lang}
                                type="button"
                                onClick={() => setLanguage(lang as 'English' | 'Spanish' | 'French' | 'German' | 'Norwegian')}
                                disabled={isGenerating || !isAuthenticated}
                                className={`flex-1 min-w-[80px] sm:min-w-[90px] py-2 sm:py-2.5 px-2 sm:px-3 rounded-lg border-2 font-semibold text-xs sm:text-sm transition-all ${
                                  language === lang
                                    ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                                    : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                <span className="mr-1 sm:mr-1.5">{flag}</span>
                                {lang}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* QA Check Option */}
                        <div className="sm:col-span-2 lg:col-span-3 mt-2">
                          <div className="bg-white/60 rounded-lg p-3 sm:p-4 border-2 border-orange-200">
                            <label className="flex items-start cursor-pointer group">
                              <div className="relative flex items-center pt-0.5">
                                <input
                                  type="checkbox"
                                  checked={qaCheck}
                                  onChange={(e) => setQaCheck(e.target.checked)}
                                  disabled={isGenerating || !isAuthenticated}
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 border-2 border-orange-300 rounded focus:ring-2 focus:ring-orange-200 disabled:opacity-50 cursor-pointer"
                                />
                              </div>
                              <div className="ml-2 sm:ml-3 flex-1">
                                <div className="flex items-center flex-wrap gap-1 sm:gap-2">
                                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-xs sm:text-sm font-semibold text-gray-800 group-hover:text-gray-900 transition-colors">
                                    Enable AI Quality Check
                                  </span>
                                  <span className="text-[10px] sm:text-xs bg-orange-600 text-white px-1.5 sm:px-2 py-0.5 rounded-full font-medium">BETA</span>
                                </div>
                                <p className="text-[10px] sm:text-xs text-gray-600 mt-1">
                                  Automatically verify all answers for accuracy and quality before generating your quiz
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!prompt.trim() || isGenerating || !isAuthenticated}
                  className="btn-primary w-full py-3 sm:py-4 text-sm sm:text-base"
                >
                  {isGenerating ? (
                    <div className="flex items-center justify-center">
                      <span className="text-xl sm:text-2xl mr-2 sm:mr-3 animate-bounce-gentle">{loadingStages[loadingStage].icon}</span>
                      <div className="flex flex-col items-start">
                        <span className="text-xs sm:text-base">{loadingStages[loadingStage].message}</span>
                        <span className="text-[10px] sm:text-xs opacity-75">
                          {Math.floor(elapsedTime / 1000)}s • Estimated: 30-45s
                        </span>
                      </div>
                    </div>
                  ) : (
                    '✨ Generate Quiz'
                  )}
                </button>
              </form>
              </div>
            </div>
          ) : null}

          {/* Loading UI with skeleton cards */}
          {isGenerating && !quiz && (
            <div className="max-w-5xl mx-auto mt-6 sm:mt-8">
              {/* Skeleton question cards */}
              <div className="space-y-4 sm:space-y-6">
                {Array.from({ length: Math.min(3, numberOfQuestions) }, (_, index) => (
                  <SkeletonQuestionCard key={index} index={index} />
                ))}

                {numberOfQuestions > 3 && (
                  <div className="text-center py-4">
                    <div className="text-gray-600 text-xs sm:text-sm font-medium">
                      + {numberOfQuestions - 3} more questions being crafted...
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {quiz && (
            <>
              {/* IMPROVEMENT 1: Success Hero Section with Clear Next Steps */}
              <div className="mb-8 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl border-2 border-green-200 overflow-hidden shadow-lg">
                <div className="p-6 sm:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Quiz Created Successfully!
                      </h2>
                      <p className="text-gray-700 text-base sm:text-lg">
                        {quiz.title}
                      </p>
                    </div>
                  </div>

                  {/* Quiz Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-green-200">
                      <div className="text-2xl sm:text-3xl font-bold text-green-700">{quiz.questions.length}</div>
                      <div className="text-xs sm:text-sm text-gray-600 font-medium">Questions</div>
                    </div>
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-green-200">
                      <div className="text-2xl sm:text-3xl font-bold text-green-700">{difficulty === 'normal' ? 'Medium' : difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}</div>
                      <div className="text-xs sm:text-sm text-gray-600 font-medium">Difficulty</div>
                    </div>
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-green-200">
                      <div className="text-2xl sm:text-3xl font-bold text-green-700">{language}</div>
                      <div className="text-xs sm:text-sm text-gray-600 font-medium">Language</div>
                    </div>
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-green-200">
                      <div className="text-2xl sm:text-3xl font-bold text-green-700">{toneOfVoice.charAt(0).toUpperCase() + toneOfVoice.slice(1)}</div>
                      <div className="text-xs sm:text-sm text-gray-600 font-medium">Tone</div>
                    </div>
                  </div>

                  {/* Collapsible Fact Check Details */}
                  {quiz.fact_check && qaCheck && (
                    <div className={`mb-6 rounded-xl border-2 overflow-hidden transition-all ${
                      quiz.fact_check.fact_check_passed
                        ? 'bg-green-50/50 border-green-200'
                        : 'bg-amber-50/50 border-amber-200'
                    }`}>
                      {/* Collapsed Header - Always Visible */}
                      <button
                        onClick={() => setIsFactCheckExpanded(!isFactCheckExpanded)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/40 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          <svg className={`w-4 h-4 ${
                            quiz.fact_check.fact_check_passed ? 'text-green-600' : 'text-amber-600'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className={`text-sm font-semibold ${
                            quiz.fact_check.fact_check_passed ? 'text-green-900' : 'text-amber-900'
                          }`}>
                            AI Quality Check
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            quiz.fact_check.fact_check_passed
                              ? 'bg-green-600 text-white'
                              : 'bg-amber-600 text-white'
                          }`}>
                            {quiz.fact_check.fact_check_passed ? 'Verified' : 'Corrected'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {(quiz.fact_check.confidence * 100).toFixed(0)}% confidence
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                            {isFactCheckExpanded ? 'Hide details' : 'View details'}
                          </span>
                          <svg
                            className={`w-4 h-4 text-gray-600 transition-transform duration-300 ${
                              isFactCheckExpanded ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/* Expandable Details */}
                      <div className={`transition-all duration-300 ease-in-out ${
                        isFactCheckExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      } overflow-hidden`}>
                        <div className="px-4 pb-4 pt-2 border-t border-white/50">
                          {quiz.fact_check.fact_check_passed ? (
                            <div className="text-sm text-green-700">
                              <p className="font-medium mb-2">✓ All questions passed verification</p>
                              <p className="text-xs text-green-600">
                                Our AI reviewed all questions and answers for accuracy and quality. No corrections were needed.
                              </p>
                            </div>
                          ) : (
                            <div className="text-sm text-amber-800">
                              <p className="font-medium mb-2">⚠️ AI Findings</p>
                              {quiz.fact_check?.findings?.length > 0 && (
                                <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
                                  <p className="text-xs font-semibold text-amber-900 mb-2">Please review:</p>
                                  <ul className="space-y-1.5">
                                    {quiz.fact_check.findings.map((finding, idx) => (
                                      <li key={idx} className="text-xs text-amber-700 flex items-start gap-2">
                                        <span className="text-amber-500 mt-0.5">•</span>
                                        <span className="flex-1">
                                          <strong>Question {finding.question_number}:</strong> {finding.message}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* IMPROVEMENT 2: Clear Primary Actions */}
                  <div className="space-y-3">
                    {/* Re-run Fact Check Button */}
                    {qaCheck && (
                      <button
                        onClick={handleRerunFactCheck}
                        disabled={isFactChecking || (!!user && user['custom:plan'] !== 'pro' && user['custom:plan'] !== 'beta')}
                        className="w-full btn-secondary py-3 text-sm sm:text-base disabled:opacity-50 flex items-center justify-center gap-2"
                        title={!!user && user['custom:plan'] !== 'pro' && user['custom:plan'] !== 'beta' ? "Fact Check is a Pro feature" : "Re-run AI-powered fact checking"}
                      >
                        {isFactChecking ? (
                          <>
                            <span className="spinner border-orange-600 border-t-orange-200"></span>
                            Checking facts...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Re-run Fact Check {user && user['custom:plan'] !== 'pro' && user['custom:plan'] !== 'beta' && '(Pro)'}
                          </>
                        )}
                      </button>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                    {!isAuthenticated ? (
                      <button
                        onClick={handleSaveQuiz}
                        className="flex-1 btn-primary py-4 text-base sm:text-lg"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                        Sign In to Save & Play
                      </button>
                    ) : savedQuizId ? (
                      <div className="flex-1 bg-green-100 border-2 border-green-300 text-green-800 rounded-xl px-6 py-4 font-semibold text-center">
                        ✓ Quiz saved! Redirecting to edit page...
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={handleSaveQuiz}
                          disabled={isSavingQuiz}
                          className="flex-1 btn-primary py-4 text-base sm:text-lg disabled:opacity-50"
                        >
                          {isSavingQuiz ? (
                            <>
                              <span className="spinner border-white border-t-green-200"></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                              </svg>
                              Save & Continue Editing
                            </>
                          )}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setQuiz(null);
                        setSavedQuizId(null);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="btn-secondary py-4 text-base sm:text-lg"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create New Quiz
                    </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* IMPROVEMENT 3: Better Visual Hierarchy for Questions */}
              <div className="mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Review Your Questions
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  Click the regenerate button to get a different question, or remove questions you don't like.
                </p>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {quiz.questions.map((question, index) => (
                  <QuestionCard key={question.id} question={question} index={index} />
                ))}
              </div>

              {/* Bottom Action Bar - Sticky */}
              <div className="sticky bottom-0 mt-8 bg-white/95 backdrop-blur-md border-t-2 border-gray-200 p-4 -mx-4 sm:-mx-6 lg:-mx-8">
                <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-3">
                  {!isAuthenticated ? (
                    <button
                      onClick={handleSaveQuiz}
                      className="flex-1 btn-primary py-3"
                    >
                      Sign In to Save This Quiz
                    </button>
                  ) : savedQuizId ? null : (
                    <button
                      onClick={handleSaveQuiz}
                      disabled={isSavingQuiz}
                      className="flex-1 btn-primary py-3 disabled:opacity-50"
                    >
                      {isSavingQuiz ? 'Saving...' : '💾 Save Quiz'}
                    </button>
                  )}
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="btn-secondary py-3"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    Back to Top
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateQuiz;